import { cn } from "@/lib/utils";

const fieldClasses =
  "border-input bg-background focus-visible:ring-ring h-10 w-full rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50 aria-[invalid=true]:border-destructive";

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(fieldClasses, className)} {...props} />;
}

export function Textarea({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(fieldClasses, "min-h-20 py-2", className)} {...props} />;
}

/**
 * A native select rather than a Radix one for phase 1: it is keyboard- and
 * screen-reader-correct for free, and nothing here needs custom option rendering yet.
 */
export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn(fieldClasses, "pr-8", className)} {...props} />;
}

/**
 * `htmlFor` is **required**, not optional: a label that is not associated with a control
 * is invisible to a screen reader and does not focus its field when clicked.
 *
 * The a11y lint rule cannot see through a wrapper component to verify the association,
 * so the type does it instead — which is the stronger guarantee, because it fails at
 * every call site rather than at this definition.
 */
export function Label({
  className,
  htmlFor,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement> & { htmlFor: string }) {
  return <label htmlFor={htmlFor} className={cn("text-sm font-medium", className)} {...props} />;
}

/** Field wrapper: label, control, and the error text in one consistent shape. */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && !error && <p className="text-muted-foreground text-xs">{hint}</p>}
      {error && (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
